import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Button, TextField, Paper, Snackbar, Alert, CircularProgress } from "@mui/material"
import { useAuth } from "../context/AuthContext"
import loginBg from "../assets/background/login-bg.png"
import logo from "../assets/logo/arlintas-logo.png"

const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login, error, setError, user } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (email && password) {
      setIsLoading(true)
      try {
        const success = await login({ email, password })
        if (success) {
          setShowSnackbar(true)
          setTimeout(() => {
            const from = location.state?.from?.pathname || "/"
            navigate(from, { replace: true })
          }, 1500)
        }
      } catch (err) {
        setError("An unexpected error occurred. Please try again.")
      } finally {
        setIsLoading(false)
      }
    } else {
      setError("Please enter both email and password.")
    }
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setShowSnackbar(false)
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundImage: `url(${loginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: 400,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
          borderRadius: "15px",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Arlintas Logo"
          sx={{
            width: "180px",
            height: "auto",
            display: "block",
            margin: "0 auto 2rem",
          }}
        />
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "28px",
                "& fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.87)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 100px rgba(30, 58, 95, 0.1) inset",
                  WebkitTextFillColor: "#000000",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#000000",
              },
              "& .MuiInputBase-input": {
                color: "#000000",
              },
            }}
          />
          <TextField
            label="Password"
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "28px",
                "& fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.87)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 100px rgba(30, 58, 95, 0.1) inset",
                  WebkitTextFillColor: "#000000",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#000000",
              },
              "& .MuiInputBase-input": {
                color: "#000000",
              },
            }}
          />
          {error && (
            <Box sx={{ mt: 1, mb: 2, color: "error.main", textAlign: "center", fontSize: "0.875rem" }}>{error}</Box>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              mt: 2,
              borderRadius: "20px",
              height: "40px",
              textTransform: "none",
              fontSize: "0.875rem",
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Log In"}
          </Button>
        </form>
      </Paper>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          Login successful! Welcome, {user?.name || "User"}!
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Login
